import { Box, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { useEdition } from '@/hooks/useEdition'
import { useTeam } from '@/hooks/useTeam'

/**
 * Tells an admin, on every researcher screen, that they are looking at the
 * app through the edition switch (`identity_app.views.me_edition_view`) and
 * not through their own plan.
 *
 * Why this exists as its own always-on banner rather than the small "Pro"
 * badge already in the sidebar: that badge is worn by real Pro customers too,
 * so an admin previewing FaceWise or Pro would see exactly what a customer on
 * that edition sees, with nothing marking the screen as a preview. MC/Marta
 * comparing the two editions is the whole point of the switch (Z-35) — a
 * ticket filed against "a bug in FaceWise" that was actually an admin account
 * left switched to it costs more than one more banner line.
 *
 * Deliberately just an indicator, not a control: the switch itself lives in
 * the profile menu (`ResearcherSidebar`), and this must not duplicate that
 * logic. Rendered for every account with `can_view_admin_panel`, matching
 * exactly who the switch is offered to (`CanViewAdminPanel` on the backend),
 * and hidden on a team seat for the same reason the menu item is: a team seat
 * is always Pro regardless of the account's own `edition` column
 * (`licensing_app.access.edition_of`), so the switch — and this banner about
 * it — would describe a state the account cannot actually be in.
 */
export default function EditionPreviewBanner() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()
  const { isBasic } = useEdition()
  const { inTeam } = useTeam()

  if (!user?.can_view_admin_panel || inTeam) return null

  const editionName = isBasic ? 'FaceWise' : 'FaceWise Pro'

  return (
    <Box
      sx={{
        mx: { xs: '20px', md: '32px' },
        mt: { xs: '16px', md: '20px' },
        px: '16px',
        py: '9px',
        borderRadius: '12px',
        bgcolor: alpha(c.navy, 0.06),
        border: `1px solid ${alpha(c.navy, 0.18)}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        flexWrap: 'wrap',
      }}
    >
      <VisibilityOutlinedIcon sx={{ fontSize: 17, color: c.navy }} />
      <Typography sx={{ fontSize: 13, color: c.navy, flex: 1, minWidth: 0 }}>
        <strong>{t('editionPreview.label')}</strong>
        {' — '}
        {t('editionPreview.body', { edition: editionName })}
      </Typography>
    </Box>
  )
}
